import map from 'lodash/map';
import forEach from 'lodash/forEach';
import { checkSchema } from '@zedoc/check-schema';
import Formula from '../Formula';
import { FORMULA_TYPE__COUNT_ANSWERED } from '../../../constants';
import { createCountAnsweredExpression } from './minAnswered';

// TECH-3934 (PFB-260): the number of answered questions in a set. An answer
// counts when it evaluates to a number (decision D3), once per answer in a
// collection. The result is a plain count (decision D4); zero is a value.

const settingsSchema = {
  type: 'object',
  required: ['operands'],
  properties: {
    operands: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id'],
        properties: {
          id: {
            type: 'string',
          },
        },
      },
    },
  },
};

class FormulaCountAnswered extends Formula {
  validate() {
    if (!this.settings) {
      return this.constructor.NotConfigured;
    }
    if (checkSchema(settingsSchema, this.settings)) {
      return this.constructor.NotConfigured;
    }
    return undefined;
  }

  evaluate(scope) {
    let n = 0;
    forEach(this.settings.operands, ({ id }) => {
      forEach(scope.pickAllAnswers(id), (answer) => {
        if (answer && !scope.evaluateAsNumber(answer.value, id).error) {
          n += 1;
        }
      });
    });
    return {
      value: n,
    };
  }

  compile(questionsHierarchy) {
    const compiled = {
      ...this,
      settings: {},
    };
    // review:manual: section mode takes every question in the section, formula questions included. A count question does not count itself (the evaluation cycle check stops it), but a formula question in the section that gives a number is counted as answered - and sum and average with a sectionId do the same, so with minAnswered 2 and one item answered, a count question in that section makes the minimum pass (probe: { value: 2 }). Whether "answered" should exclude formula questions changes decision D3 and is left for that decision.
    if (this.meta && this.meta.sectionId) {
      compiled.settings = {
        operands: questionsHierarchy.mapQuestions(
          (q) => ({
            id: q.id,
          }),
          {
            sectionId: this.meta.sectionId,
          },
        ),
      };
    } else if (this.meta && this.meta.questionIds) {
      compiled.settings = {
        operands: map(this.meta.questionIds, (questionId) => ({
          id: questionId,
        })),
      };
    }
    return compiled;
  }

  toMongoExpression() {
    return createCountAnsweredExpression(this.settings.operands);
  }

  static createMapSettings(mapQuestionId) {
    return (value, key) => {
      switch (key) {
        case 'operands':
          return map(value, (operand) => ({
            ...operand,
            id: mapQuestionId(operand.id),
          }));
        default:
          return value;
      }
    };
  }

  static createMapMeta(mapQuestionId) {
    return (value, key) => {
      switch (key) {
        case 'sectionId':
          return mapQuestionId(value);
        case 'questionIds':
          return map(value, mapQuestionId);
        default:
          return value;
      }
    };
  }
}

Formula.types[FORMULA_TYPE__COUNT_ANSWERED] = FormulaCountAnswered;

export default FormulaCountAnswered;
